import { useNostrPublish } from '@/hooks/useNostrPublish';
import { NostrEvent } from '@nostrify/nostrify';
import { useToast } from '@/hooks/useToast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { useState } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';

interface DeleteListDialogProps {
  listEvent: NostrEvent;
  onDeleted?: () => void;
  children: React.ReactNode;
}

export const DeleteListDialog = ({ listEvent, onDeleted, children }: DeleteListDialogProps) => {
  const { mutate: publishEvent, isPending } = useNostrPublish();
  const { toast } = useToast();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const { user } = useCurrentUser();

  const handleDelete = () => {
    publishEvent(
      {
        kind: 5,
        tags: [['e', listEvent.id]],
        content: 'Deleted deck list',
      },
      {
        onSuccess: () => {
          toast({ title: "List deleted successfully!" });
          if (onDeleted) {
            onDeleted();
          }
        },
        onError: () => {
          toast({ title: "Failed to delete list", variant: "destructive" });
        },
        onSettled: () => {
          setIsAlertOpen(false);
        }
      }
    );
  };

  if (user?.pubkey !== listEvent.pubkey) {
    return null;
  }

  return (
    <AlertDialog open={isAlertOpen} onOpenChange={setIsAlertOpen}>
      <AlertDialogTrigger asChild>
        {children}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure you want to delete this list?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently delete the list from all relays.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} disabled={isPending}>
            {isPending ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
